import _slicedToArray from "/home/jenkins/agent/workspace/ite-static-site-generator_master/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import FocusTrap from 'focus-trap-react';
import Portal from '@clearscore-group/lib.helpers.portal';
var PortalCoreContext = /*#__PURE__*/createContext(undefined);
var usePortalContext = function usePortalContext() {
  var context = useContext(PortalCoreContext);
  if (!context) {
    throw new Error('PortalCore compound components must be rendered within the PortalCore component');
  }
  return context;
};
var useIsTransitioningOut = function useIsTransitioningOut() {
  var _usePortalContext = usePortalContext(),
    isOpen = _usePortalContext.isOpen,
    _usePortalContext$tra = _usePortalContext.transitionOutDuration,
    transitionOutDuration = _usePortalContext$tra === void 0 ? 0 : _usePortalContext$tra;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    willTransitionOut = _useState2[0],
    setWillTransitionOut = _useState2[1];
  var timeout = useRef();
  useEffect(function () {
    if (isOpen && transitionOutDuration > 0) {
      setWillTransitionOut(true);
    }
    if (!isOpen && transitionOutDuration > 0) {
      timeout.current = setTimeout(function () {
        return setWillTransitionOut(false);
      }, transitionOutDuration);
    }
    return function () {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [isOpen, transitionOutDuration]);
  var isTransitioningOut = willTransitionOut && !isOpen;
  return {
    isTransitioningOut: isTransitioningOut
  };
};
var Backdrop = function Backdrop(_ref) {
  var children = _ref.children;
  var _usePortalContext2 = usePortalContext(),
    _usePortalContext2$se = _usePortalContext2.setIsOpen,
    setIsOpen = _usePortalContext2$se === void 0 ? function (value) {
      return value;
    } : _usePortalContext2$se,
    isOpen = _usePortalContext2.isOpen,
    isCloseable = _usePortalContext2.isCloseable;
  var _useIsTransitioningOu = useIsTransitioningOut(),
    isTransitioningOut = _useIsTransitioningOu.isTransitioningOut;
  if (!(isOpen || isTransitioningOut)) return null;
  return /*#__PURE__*/React.createElement(Portal, null, isCloseable ? /*#__PURE__*/React.createElement("div", {
    onClick: function onClick() {
      setIsOpen(false);
    }
  }, children) : children);
};
var Toggle = function Toggle(_ref2) {
  var children = _ref2.children,
    _ref2$onClick = _ref2.onClick,
    onClickAction = _ref2$onClick === void 0 ? null : _ref2$onClick,
    _ref2$asChild = _ref2.asChild,
    _asChild = _ref2$asChild === void 0 ? false : _ref2$asChild;
  var _usePortalContext3 = usePortalContext(),
    _usePortalContext3$se = _usePortalContext3.setIsOpen,
    setIsOpen = _usePortalContext3$se === void 0 ? function (value) {
      return value;
    } : _usePortalContext3$se,
    isOpen = _usePortalContext3.isOpen,
    isCloseable = _usePortalContext3.isCloseable;
  if (!isCloseable && isOpen) {
    return children;
  }
  var onClick = function onClick(event) {
    if (onClickAction) {
      onClickAction(event);
    }
    setIsOpen(!isOpen);
  };
  if (_asChild) {
    return /*#__PURE__*/React.cloneElement(children, {
      onClick: onClick
    });
  }
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: onClick
  }, children);
};

/**
 * If the portal is animated there is a small window where the focus-trap
 * within cannot be focussed. To handle this we can pass a function
 * returning a promise that resolves after x period of time, after
 * which the trap can be focussed. Default is that it immediately
 * resolves
 */
var defaultFocusableTimeout = function defaultFocusableTimeout() {
  return Promise.resolve();
};
var defaultFocusOptions = {
  fallbackFocus: 'body',
  allowOutsideClick: true,
  checkCanFocusTrap: defaultFocusableTimeout,
  initialFocus: undefined
};
var Content = function Content(_ref3) {
  var ariaLabel = _ref3.ariaLabel,
    ariaLabelledBy = _ref3.ariaLabelledBy,
    ariaDescribedBy = _ref3.ariaDescribedBy,
    children = _ref3.children,
    focusOptions = _ref3.focusOptions;
  var _usePortalContext4 = usePortalContext(),
    isOpen = _usePortalContext4.isOpen;
  var _useIsTransitioningOu2 = useIsTransitioningOut(),
    isTransitioningOut = _useIsTransitioningOu2.isTransitioningOut;
  if (!(isOpen || isTransitioningOut)) return null;
  return /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(FocusTrap, {
    active: isOpen,
    focusTrapOptions: _objectSpread(_objectSpread({}, defaultFocusOptions), focusOptions)
  }, /*#__PURE__*/React.createElement("div", {
    role: "dialog",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy
  }, children)));
};

/*
    isOpenOverride: Force the state of the portal to be open or closed
    transitionOutDuration: Time delay before the component is removed from the DOM
    onClose, onOpen: functions triggered when the portal is open / closed
 */

var PortalCore = function PortalCore(_ref4) {
  var children = _ref4.children,
    _ref4$isOpenOverride = _ref4.isOpenOverride,
    _isOpenOverride = _ref4$isOpenOverride === void 0 ? false : _ref4$isOpenOverride,
    _ref4$transitionOutDu = _ref4.transitionOutDuration,
    _transitionOutDuration = _ref4$transitionOutDu === void 0 ? 0 : _ref4$transitionOutDu,
    _ref4$onClose = _ref4.onClose,
    _onClose = _ref4$onClose === void 0 ? undefined : _ref4$onClose,
    _ref4$onOpen = _ref4.onOpen,
    _onOpen = _ref4$onOpen === void 0 ? undefined : _ref4$onOpen,
    _ref4$isCloseable = _ref4.isCloseable,
    _isCloseable = _ref4$isCloseable === void 0 ? true : _ref4$isCloseable;
  var _useState3 = useState(_isOpenOverride),
    _useState4 = _slicedToArray(_useState3, 2),
    isOpen = _useState4[0],
    setIsOpen = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    hasBeenOpened = _useState6[0],
    setHasBeenOpened = _useState6[1];
  var _useState7 = useState(_isOpenOverride),
    _useState8 = _slicedToArray(_useState7, 2),
    isOpenTransition = _useState8[0],
    setIsOpenTransition = _useState8[1];
  var timeout = useRef();
  useEffect(function () {
    setIsOpen(_isOpenOverride);
  }, [_isOpenOverride]);
  useEffect(function () {
    var handleEsc = function handleEsc(event) {
      var escKey = 27;
      if (event.keyCode === escKey && _isCloseable) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return function () {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [_isCloseable]);
  useEffect(function () {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      timeout.current = setTimeout(function () {
        return setIsOpenTransition(true);
      }, 0);
      setHasBeenOpened(true);
    }
    if (!isOpen) {
      document.body.style.overflow = 'unset';
      timeout.current = setTimeout(function () {
        return setIsOpenTransition(false);
      }, 0);
    }
    if (_onClose && !isOpen && _isCloseable && hasBeenOpened) {
      _onClose();
    }
    if (_onOpen && isOpen) {
      _onOpen();
    }
    return function () {
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  var value = React.useMemo(function () {
    return {
      isOpen: isOpen,
      setIsOpen: setIsOpen,
      isOpenTransition: isOpenTransition,
      transitionOutDuration: _transitionOutDuration,
      isCloseable: _isCloseable
    };
  }, [_isCloseable, isOpen, isOpenTransition, _transitionOutDuration]);
  return /*#__PURE__*/React.createElement(PortalCoreContext.Provider, {
    value: value
  }, children);
};
PortalCore.Backdrop = Backdrop;
PortalCore.Toggle = Toggle;
PortalCore.Content = Content;
PortalCore.useContext = usePortalContext;
export default PortalCore;